import React, { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import Topbar from "@/components/Topbar";
import TotoAvatar from "@/components/TotoAvatar";
import { useTotoAssets } from "@/context/TotoAssetsContext";
import { Upload, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATES = [
  { key: "idle", label: "Idle", hint: "The default at-rest pose" },
  { key: "thinking", label: "Thinking", hint: "While TOTO is generating a reply" },
  { key: "happy", label: "Happy", hint: "After a successful action / completed reply" },
  { key: "curious", label: "Curious", hint: "Empty states, first-time prompts" },
  { key: "alert", label: "Alert", hint: "Errors, warnings, offline moments" },
  { key: "celebrating", label: "Celebrating", hint: "Payroll export, milestones" },
  { key: "helping", label: "Helping", hint: "Explainer cards, walkthroughs" },
  { key: "looks_up", label: "Looks up", hint: "Optional · pose from mockup" },
  { key: "tail_wags", label: "Tail wags", hint: "Optional · pose from mockup" },
  { key: "walking", label: "Walking", hint: "Optional · pose from mockup" },
  { key: "wordmark", label: "Wordmark", hint: "Optional · 'TOTO' + paw-dot lockup" },
  { key: "app_icon", label: "App icon", hint: "Optional · square icon for favicon / PWA" },
];

export default function TotoAssets() {
  const { assets, refresh } = useTotoAssets();

  return (
    <div>
      <Topbar title="TOTO Studio" />
      <div className="px-4 sm:px-10 pb-12 sm:pb-16 space-y-8 stagger-fade">
        <section className="bg-white subtle-border rounded-2xl p-8 card-shadow flex items-start gap-6">
          <TotoAvatar state="helping" size={56} className="text-stone-700" />
          <div>
            <h2 className="font-serif-display text-3xl text-stone-900">Bring TOTO to life</h2>
            <p className="text-stone-500 mt-3 max-w-xl leading-relaxed">
              Upload your designer's SVG (preferred) or PNG for each TOTO state. When a state has artwork,
              TOTO will use it everywhere — sidebar, login, chat, celebrations. States without artwork keep
              the minimal placeholder.
            </p>
            <div className="label-eyebrow mt-4">SVG · PNG · JPG · WEBP · up to 500KB per state</div>
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="toto-asset-grid">
          {STATES.map((s) => (
            <AssetSlot key={s.key} stateInfo={s} asset={assets[s.key]} onChange={refresh} />
          ))}
        </section>
      </div>
    </div>
  );
}

function AssetSlot({ stateInfo, asset, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/toto/assets/${stateInfo.key}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await onChange();
      toast.success(`${stateInfo.label} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const remove = async () => {
    if (!asset) return;
    try {
      await api.delete(`/toto/assets/${stateInfo.key}`);
      await onChange();
      toast.success(`${stateInfo.label} removed — using placeholder`);
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="bg-white subtle-border rounded-2xl p-6 card-shadow" data-testid={`asset-slot-${stateInfo.key}`}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-stone-900 text-base font-serif-display">{stateInfo.label}</div>
          <div className="label-eyebrow mt-1">{stateInfo.hint}</div>
        </div>
        {asset && <CheckCircle2 size={14} strokeWidth={1.25} className="text-emerald-600" />}
      </div>

      <div className="rounded-xl bg-stone-50 border border-stone-100 h-32 flex items-center justify-center mb-4">
        <TotoAvatar state={stateInfo.key} size={72} className="text-stone-700" />
      </div>

      <label
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
        className="flex flex-col items-center justify-center gap-2 px-4 py-4 rounded-xl border border-dashed border-stone-200 bg-stone-50/40 hover:bg-stone-50 cursor-pointer transition-colors"
        data-testid={`asset-dropzone-${stateInfo.key}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".svg,.png,.jpg,.jpeg,.webp,image/svg+xml,image/png,image/jpeg,image/webp"
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
          data-testid={`asset-input-${stateInfo.key}`}
        />
        <Upload size={16} strokeWidth={1.25} className="text-stone-400" />
        <div className="text-xs text-stone-600">
          {uploading ? "Uploading…" : asset ? "Replace file" : "Drop a file, or click"}
        </div>
        {asset?.filename && (
          <div className="text-[10px] text-stone-400 truncate max-w-full">{asset.filename} · {Math.round((asset.size || 0) / 1024)}KB</div>
        )}
      </label>

      {asset && (
        <button
          onClick={remove}
          data-testid={`asset-remove-${stateInfo.key}`}
          className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs text-stone-500 hover:text-rose-500 py-2 rounded-full transition-colors"
        >
          <Trash2 size={12} strokeWidth={1.25} /> Remove & use placeholder
        </button>
      )}
    </div>
  );
}
